/*
 Тут мы превращаем данные таблицы с сервера в наши колонки и строки.

 Делаем всё ровно так же, как делала старая таблица при загрузке: собираем
 колонки в группы по их номеру группы, склеиваем названия через запятую, слева
 ставим колонку с чекбоксами. Это нужно, чтобы старые ячейки рисовались как
 раньше. Ширины колонок тут не считаем, этим занимается браузер, сортировку и
 фильтрацию делает сервер.
*/

import { computed, shallowRef, type ComputedRef, type ShallowRef } from "vue";
import type {
  AnalyticsResultTableExtended,
  CellDataParameter,
  TableDataCell,
  TableDataCellContainer,
  TableDataRowExtended,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import type {
  TableHeaderCell,
  TableHeaderCellContainer,
} from "@/models/interfaces/entity-settings/querytable/tableheader";
import { ColumnCategory, TableDataCellDisplayType } from "@/models/etc/types";
import { getSettingsTypeName } from "@/models/helpers/type-names";
import { trimEllip } from "@/models/helpers/recursive";
import type { LiteColumn, LiteRow, LiteCellGroup } from "../types";

export interface UseTableModelArgs {
  /** данные таблицы с сервера. могут меняться при перезагрузке, сортировке, смене страницы */
  source: () => AnalyticsResultTableExtended | undefined;
  /** есть ли у пользователя права на правку. от этого зависит, будет ли колонка с чекбоксами */
  hasWriteAccess: () => boolean;
  /** тип выбранного элемента, нужен чтобы получать названия колонок */
  destType: () => number;
}

export interface UseTableModelResult {
  /** колонки, вместе с колонкой чекбоксов слева, если есть права на правку */
  columns: ComputedRef<LiteColumn[]>;
  /** строки. Перерисовываются только когда меняем весь список целиком */
  rows: ShallowRef<LiteRow[]>;
  /** есть ли вообще колонки, по которым можно сортировать, нужно для верхней панели */
  hasSortColumns: ComputedRef<boolean>;
  /** подменить одну строку по её номеру */
  replaceRow: (entityId: number, newRow: LiteRow) => void;
  /** полностью сбросить, например при смене страницы или обновлении */
  reset: () => void;
  /** пересобрать строки заново, если данные поменяли прямо на месте, как делает наш стор */
  rebuildRows: () => void;
}

const MULTIEDIT_COLUMN_ID = "multiedit";
const COMPOSITE_CATEGORIES_NEEDING_ENTITY_NAMES = [3, 4, 5, 12, 45];
const TWO_WORD_COMPOSITE_CATEGORIES = [12, 45];

export function useTableModel(args: UseTableModelArgs): UseTableModelResult {
  // меняем список строк целиком, а не следим за каждой строкой по отдельности.
  // так таблица перерисовывается заметно дешевле.
  const rows = shallowRef<LiteRow[]>([]);

  const columns = computed<LiteColumn[]>(() => {
    const src = args.source();
    if (!src || !src.HeaderRow || !src.HeaderRow.length) return [];

    const out: LiteColumn[] = [];
    const destType = args.destType();
    const wa = args.hasWriteAccess();

    if (wa) {
      // колонка с чекбоксами слева. номер у неё постоянный, чтобы старые ячейки её узнавали
      out.push({
        id: MULTIEDIT_COLUMN_ID,
        text: "",
        sort: false,
        order: 0,
        sortEntityIds: [],
        isMultiedit: true,
      });
    }

    let prevGroupId = -1;
    let tmp: LiteColumn | undefined = undefined;
    let hind = 0;

    src.HeaderRow.forEach((el: TableHeaderCellContainer) => {
      el.Cells.forEach((cont: TableHeaderCell) => {
        const sameGroup =
          (cont.GroupId !== undefined && cont.GroupId > 0 && cont.GroupId === prevGroupId) ||
          (el.GroupId !== undefined && el.GroupId > 0 && el.GroupId === prevGroupId);

        // Колонка сгруппирована, если у ячейки или её строки есть GroupId. По таким
        // колонкам сортировки нет — иконку не показываем (sort=false), даже если
        // сервер прислал OrderMode > 0.
        const grouped =
          (cont.GroupId !== undefined && cont.GroupId > 0) ||
          (el.GroupId !== undefined && el.GroupId > 0);

        if (sameGroup) {
          // дописываем название к уже начатой группе, как делала старая таблица
          if (tmp !== undefined) {
            const mergedEntityIds = Array.isArray(cont.Entities)
              ? cont.Entities.map((e) => e.Id)
              : [];
            tmp.sortEntityIds = Array.from(new Set([...tmp.sortEntityIds, ...mergedEntityIds]));
            tmp.text +=
              ", " +
              (!COMPOSITE_CATEGORIES_NEEDING_ENTITY_NAMES.includes(cont.Category)
                ? getSettingsTypeName(cont.Category, destType || 0)
                : (!TWO_WORD_COMPOSITE_CATEGORIES.includes(cont.Category)
                    ? getSettingsTypeName(cont.Category, destType || 0) + " "
                    : "") +
                  (Array.isArray(cont.Entities) ? cont.Entities : [])
                    .filter((_e, ei) => [45].includes(cont.Category) && ei > 0)
                    .map((e) => trimEllip(e.Name, 35))
                    .join(TWO_WORD_COMPOSITE_CATEGORIES.includes(cont.Category) ? "." : ", "));
          } else {
            // начинаем новую группу, когда ещё ничего не накоплено
            tmp = {
              id: String(hind),
              text: getSettingsTypeName(cont.Category, destType || 0) + ":\n",
              sort: cont.OrderMode > 0 && !grouped,
              order: cont.OrderMode,
              sortCategory: cont.Category,
              dataType: cont.DataType,
              sortEntityIds: Array.isArray(cont.Entities) ? cont.Entities.map((e) => e.Id) : [],
            };
            hind++;
          }
        } else {
          // отдельная колонка, не из группы
          if (tmp !== undefined) {
            out.push(tmp);
            tmp = undefined;
          }
          tmp = {
            id: String(hind),
            text: !COMPOSITE_CATEGORIES_NEEDING_ENTITY_NAMES.includes(cont.Category)
              ? getSettingsTypeName(cont.Category, destType || 0)
              : (!TWO_WORD_COMPOSITE_CATEGORIES.includes(cont.Category)
                  ? getSettingsTypeName(cont.Category, destType || 0) + ": "
                  : "") +
                (Array.isArray(cont.Entities) ? cont.Entities : [])
                  .map((e) => trimEllip(e.Name, 35))
                  .join(TWO_WORD_COMPOSITE_CATEGORIES.includes(cont.Category) ? ".<br/>" : ","),
            sort: cont.OrderMode > 0 && !grouped,
            order: cont.OrderMode,
            sortCategory: cont.Category,
            dataType: cont.DataType,
            sortEntityIds: Array.isArray(cont.Entities) ? cont.Entities.map((e) => e.Id) : [],
          };
          hind++;
        }

        prevGroupId =
          cont.GroupId !== undefined && cont.GroupId > 0
            ? cont.GroupId
            : el.GroupId !== undefined && el.GroupId > 0
            ? el.GroupId
            : -1;
      });
    });

    if (tmp !== undefined) out.push(tmp);
    return out;
  });

  const hasSortColumns = computed<boolean>(() => columns.value.some((c) => c.sort));

  function rebuildRows() {
    const src = args.source();
    if (!src || !src.DataRows) {
      rows.value = [];
      return;
    }

    const wa = args.hasWriteAccess();
    const result: LiteRow[] = [];

    type TmpData = { key: string; group: LiteCellGroup };

    src.DataRows.forEach((row: TableDataRowExtended) => {
      const cells: Record<string, LiteCellGroup> = {};

      if (wa) {
        cells[MULTIEDIT_COLUMN_ID] = {
          entityId: row.EntityId,
          creatorId: row.CreatorId,
          subItems: [], // ячейка с чекбоксом пустая, чекбокс рисует сама таблица
        };
      }
      let columnI = -1;
      // временная переменная, чтобы копить ячейки одной колонки внутри прохода по строке
      let tmpData: TmpData | undefined = undefined;
      let prevDataGroupId = -1;

      row.CellContainers.forEach((cont: TableDataCellContainer) => {
        const isNewColumn =
          cont.GroupId === undefined ||
          cont.GroupId <= 0 ||
          cont.GroupId !== prevDataGroupId ||
          tmpData === undefined;

        if (isNewColumn) {
          if (tmpData !== undefined) {
            cells[tmpData.key] = tmpData.group;
          }
          columnI++;
          tmpData = {
            key: String(columnI),
            group: {
              entityId: row.EntityId,
              creatorId: row.CreatorId,
              subItems: [],
            },
          };
        }
        const currentGroup = (tmpData as TmpData).group;
        cont.Cells.forEach((cell: TableDataCell) => {
          currentGroup.subItems.push(cell);
          prevDataGroupId =
            cont.GroupId !== undefined && cont.GroupId > 0
              ? cont.GroupId
              : cell.GroupId !== undefined && cell.GroupId > 0
              ? cell.GroupId
              : -1;
        });
      });

      if (tmpData !== undefined) {
        const td = tmpData as TmpData;
        cells[td.key] = td.group;
      }

      // сервер не присылает готовый признак связи параметров, склеиваем его сами
      // из набора номеров. Без этого не работает подсветка связанных параметров в
      // разных колонках. Делаем так же, как старая таблица.
      buildParentRecordIds(cells);

      // приводим параметры в разных колонках одной строки к одному виду: одинаковое
      // число и одинаковый порядок. Тогда они сразу стоят друг напротив друга по
      // вертикали, без лишних пересчётов. А высоту уже подгоняет сама строка (см. useRowParameterHeights).
      // Выравнивание высот включаем, если параметры стоят горизонтально в разных
      // колонках: либо это обычные верхнеуровневые параметры (их и нормализуем по
      // позициям), либо параметры внутри одиночных рекурсивных ячеек (тип 16),
      // когда такие ячейки лежат в РАЗНЫХ колонках (а не стопкой в одной
      // сгруппированной колонке). И в том, и в другом случае подстроки стоят бок о
      // бок и должны равняться по высоте, как верхнеуровневые.
      const hasTopLevelAlignment = normalizeParameterCellsAcrossColumns(cells);
      const hasStandaloneType16Alignment = normalizeStandaloneStorageParamsAcrossColumns(cells);
      const hasParameterAlignment = hasTopLevelAlignment || hasStandaloneType16Alignment;

      result.push({
        entityId: row.EntityId,
        creatorId: row.CreatorId,
        cells,
        hasParameterAlignment,
      });
    });

    rows.value = Object.freeze(result) as LiteRow[];
  }

  function replaceRow(entityId: number, newRow: LiteRow) {
    const idx = rows.value.findIndex((r) => r.entityId === entityId);
    if (idx < 0) return;
    // делаем новый список, чтобы таблица заметила изменение.
    // остальные строки останутся на месте, перерисуется только эта.
    const next = rows.value.slice();
    next[idx] = newRow;
    rows.value = Object.freeze(next) as LiteRow[];
  }

  function reset() {
    rows.value = [];
  }

  return { columns, rows, hasSortColumns, replaceRow, reset, rebuildRows };
}

/** номер колонки с чекбоксами, выносим наружу, чтобы не писать одну и ту же строку в разных местах */
export const MULTIEDIT_COL_ID = MULTIEDIT_COLUMN_ID;

/*
 Готовим данные: сервер присылает только набор номеров, а нам нужен из них один
 склеенный ключ для каждого параметра. Без этого ключа не работает подсветка
 связанных параметров в разных колонках, потому что подсветка сравнивает как раз
 этот ключ.

 Делаем так же, как старая таблица:
   - из набора номеров собираем один ключ, просто склеивая их;
   - пустой ключ считаем как будто его нет;
   - у параметров хранилища к ключу ещё добавляем номер самого хранилища, чтобы
     ключи не совпадали между разными хранилищами.

 Меняем данные прямо на месте, потому что они и так используются только внутри
 этой строки.
*/
function buildParentRecordIds(cells: Record<string, LiteCellGroup>): void {
  // счётчики для отладки: сколько ключей удалось собрать на эту строку. Видно в
  // консоли, если включить отладку (смотри ниже).
  let dbgBuilt = 0;
  let dbgRawArrayHits = 0;
  let dbgPreserved = 0;
  for (const groupKey of Object.keys(cells)) {
    const g = cells[groupKey];
    for (const cell of g.subItems) {
      const data = cell.Data as any[];
      if (!data || !data.length) continue;

      // обычные параметры
      for (const dd of data) {
        if (!dd) continue;
        if (dd.ParentRecordIds && Array.isArray(dd.ParentRecordIds)) {
          dd.ParentRecordId = dd.ParentRecordIds.join("");
          dbgRawArrayHits++;
        } else if (dd.ParentRecordId === undefined) {
          // не затираем ключ, если он уже был проставлен раньше
          dd.ParentRecordId = undefined;
        } else {
          dbgPreserved++;
        }
        if (dd.ParentRecordId === "") dd.ParentRecordId = undefined;
        if (dd.ParentRecordId !== undefined) dbgBuilt++;
      }

      // параметры хранилища, у них данные лежат ещё на уровень глубже.
      // к ключу добавляем номер хранилища, чтобы ключи не совпадали между разными
      // хранилищами в одной колонке.
      if (cell.DisplayType === TableDataCellDisplayType.DataOfStorageItem &&
          cell.Category === ColumnCategory.StorageItemNameParameters) {
        for (const stor_cell of data as any[]) {
          const tmp_pid = stor_cell?.ItemId;
          const inner = stor_cell?.ItemData as any[];
          if (!Array.isArray(inner)) continue;
          for (const dd1 of inner) {
            if (!dd1) continue;
            if (dd1.ParentRecordIds && Array.isArray(dd1.ParentRecordIds)) {
              dd1.ParentRecordId = (dd1.ParentRecordIds.join("") || "") + tmp_pid;
              dbgRawArrayHits++;
            } else if (dd1.ParentRecordId === undefined) {
              dd1.ParentRecordId = undefined;
            } else {
              dbgPreserved++;
            }
            if (dd1.ParentRecordId === "") dd1.ParentRecordId = undefined;
            if (dd1.ParentRecordId !== undefined) dbgBuilt++;
          }
        }
      }
    }
  }
  // пишем в консоль только если включена отладка. Чтобы она зря не работала у
  // пользователей, включают её руками из консоли:
  //   window.__qtlDebugHighlight = true; // включить
  //   window.__qtlDebugHighlight = false; // выключить
  if ((typeof window !== "undefined") && (window as any).__qtlDebugHighlight) {
    // eslint-disable-next-line no-console
    console.debug(
      "[QTL highlight] buildParentRecordIds row:",
      { built: dbgBuilt, fromRawArray: dbgRawArrayHits, preserved: dbgPreserved },
    );
  }
}

/*
 Приводим параметры в разных колонках одной строки к одному виду.

 Раньше старая таблица выравнивала их тяжело: гоняла по кругу события между
 ячейками и пересчитывала высоты с задержками и копированием данных. Это медленно
 и плохо дружит с подгрузкой строк по мере прокрутки.

 Тут делаем дёшево и один раз, прямо когда собираем строки:
   1. собираем по всем таким ячейкам строки общий упорядоченный список ключей;
   2. в каждой ячейке выстраиваем параметры по этому общему списку, по одному на
      ключ. Если в данных параметра нет, ставим пустышку.

 Что это даёт: параметры в разных колонках идут в одном порядке и стоят друг
 напротив друга по вертикали. А высоту, если где-то текст длиннее, дополнительно
 подгоняет сама строка (смотри useRowParameterHeights).

 Возвращаем true, если в строке параметров несколько и их высоты надо
 согласовывать. По этому признаку мы не вешаем слежение на все остальные строки и
 не грузим всё лишний раз.
*/
function normalizeParameterCellsAcrossColumns(
  cells: Record<string, LiteCellGroup>,
): boolean {
  const PARAM_DT = TableDataCellDisplayType.Parameter;
  type ParamLoc = { groupKey: string; subIdx: number; cell: TableDataCell };
  const paramLocs: ParamLoc[] = [];

  for (const groupKey of Object.keys(cells)) {
    const g = cells[groupKey];
    g.subItems.forEach((cell, subIdx) => {
      if (cell.DisplayType === PARAM_DT) {
        paramLocs.push({ groupKey, subIdx, cell });
      }
    });
  }

  // выравнивать имеет смысл только между параметрами в РАЗНЫХ колонках (горизонтально).
  // если все параметрические ячейки в одной колонке, они стоят вертикально друг под
  // другом (как отдельные поля), и выравнивать их нельзя: иначе ячейка с несколькими
  // значениями на один ParentRecordId (например s/n) теряет строки, а соседние поля
  // раздуваются пустыми подстроками.
  const distinctColumns = new Set(paramLocs.map((l) => l.groupKey));
  if (paramLocs.length < 2 || distinctColumns.size < 2) return false;

  // пустой ключ считаем как будто параметр ни с чем не связан
  const keyOf = (recId: unknown): string | undefined => {
    if (recId === undefined || recId === null || recId === "") return undefined;
    return String(recId);
  };

  const originals = new Map<ParamLoc, CellDataParameter[]>();
  let targetLen = 0;
  const keySets: Array<Set<string>> = [];
  let hasAnyKey = false;
  for (const loc of paramLocs) {
    const data = (loc.cell.Data || []) as CellDataParameter[];
    originals.set(loc, data);
    if (data.length > targetLen) targetLen = data.length;
    const s = new Set<string>();
    for (const item of data) {
      const k = keyOf(item?.ParentRecordId);
      if (!k) continue;
      s.add(k);
      hasAnyKey = true;
    }
    keySets.push(s);
  }

  // смотрим, есть ли хоть один общий ключ хотя бы у двух колонок
  let hasCommon = false;
  outer: for (let i = 0; i < keySets.length; i++) {
    for (let j = i + 1; j < keySets.length; j++) {
      const a = keySets[i];
      const b = keySets[j];
      if (!a.size || !b.size) continue;
      const [small, large] = a.size <= b.size ? [a, b] : [b, a];
      for (const k of small) {
        if (large.has(k)) {
          hasCommon = true;
          break outer;
        }
      }
    }
  }
  // если общих ключей нет (или ключей нет вообще), выравниваем только по числу
  // параметров: добавляем пустышки до самой длинной колонки, и всё.
  if (!hasAnyKey || !hasCommon) {
    for (const loc of paramLocs) {
      const original = originals.get(loc) || [];
      const normalized = original.slice();
      while (normalized.length < targetLen) {
        normalized.push({
          ParentRecordId: undefined,
          Value: "",
          ValueType: 0,
          __placeholder: true,
        } as unknown as CellDataParameter);
      }
      const group = cells[loc.groupKey];
      if (group) {
        group.subItems = group.subItems.slice();
        group.subItems[loc.subIdx] = { ...loc.cell, Data: normalized };
      }
    }
    return true;
  }

  // общие ключи есть: берём за основу самую длинную колонку и по ней выстраиваем
  // порядок, чтобы связанные параметры встали друг напротив друга, но не плодим
  // лишние пустые строки из-за чужих ключей.
  let baseLoc = paramLocs[0];
  for (const loc of paramLocs) {
    const len = (originals.get(loc) || []).length;
    const baseLen = (originals.get(baseLoc) || []).length;
    if (len > baseLen) baseLoc = loc;
  }
  const baseData = originals.get(baseLoc) || [];
  const baseKeys: string[] = [];
  const recIdByKey = new Map<string, CellDataParameter["ParentRecordId"]>();
  for (const item of baseData) {
    const k = keyOf(item?.ParentRecordId);
    if (!k) continue;
    if (!recIdByKey.has(k)) {
      recIdByKey.set(k, item.ParentRecordId);
      baseKeys.push(k);
    }
  }

  // сколько подстрок с одним и тем же ParentRecordId максимум встречается внутри
  // одной колонки. Один ParentRecordId может иметь несколько значений (несколько
  // строк-детей у одной записи): под каждое нужен отдельный слот, иначе лишние
  // значения кладутся поверх одного ключа и теряются (виден только первый).
  const maxCountByKey = new Map<string, number>();
  for (const loc of paramLocs) {
    const counts = new Map<string, number>();
    for (const item of (originals.get(loc) || []) as CellDataParameter[]) {
      const k = keyOf(item?.ParentRecordId);
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    for (const [k, c] of counts) {
      if (c > (maxCountByKey.get(k) || 0)) maxCountByKey.set(k, c);
    }
  }

  // разворачиваем ключи базовой колонки с учётом кратности: один ключ → столько
  // слотов, сколько максимум значений этого ключа встретилось в какой-либо колонке.
  const baseSlotKeys: string[] = [];
  for (const k of baseKeys) {
    const cnt = Math.max(1, maxCountByKey.get(k) || 1);
    for (let i = 0; i < cnt; i++) baseSlotKeys.push(k);
  }

  const slotCount = Math.max(targetLen, baseSlotKeys.length);
  const slotKey = Array.from({ length: slotCount }, (_v, i) => (i < baseSlotKeys.length ? baseSlotKeys[i] : undefined));
  const baseKeySet = new Set(baseKeys);

  for (const loc of paramLocs) {
    const original = originals.get(loc) || [];
    const byKey = new Map<string, CellDataParameter[]>();
    const leftovers: CellDataParameter[] = [];
    for (const item of original) {
      const k = keyOf(item?.ParentRecordId);
      if (k && baseKeySet.has(k)) {
        const arr = byKey.get(k) || [];
        arr.push(item);
        byKey.set(k, arr);
      } else {
        leftovers.push(item);
      }
    }

    const normalized: CellDataParameter[] = new Array(slotCount);
    for (let i = 0; i < slotCount; i++) {
      const k = slotKey[i];
      if (!k) continue;
      const arr = byKey.get(k);
      if (arr && arr.length) normalized[i] = arr.shift()!;
    }

    // оставшиеся параметры, что ни с чем не связаны, докладываем в пустые места по
    // порядку, чтобы не было длинных кусков пустоты.
    for (let i = 0; i < slotCount && leftovers.length; i++) {
      if (!normalized[i]) normalized[i] = leftovers.shift()!;
    }

    for (let i = 0; i < slotCount; i++) {
      if (normalized[i]) continue;
      normalized[i] = {
        ParentRecordId: slotKey[i] ? recIdByKey.get(slotKey[i]!) : undefined,
        Value: "",
        ValueType: 0,
        __placeholder: true,
      } as unknown as CellDataParameter;
    }

    const group = cells[loc.groupKey];
    if (group) {
      group.subItems = group.subItems.slice();
      group.subItems[loc.subIdx] = { ...loc.cell, Data: normalized };
    }
  }

  return true;
}

/*
  Параметры внутри ОДИНОЧНЫХ рекурсивных ячеек склада (тип 16), лежащих в РАЗНЫХ
  колонках. Одиночная колонка — это колонка ровно с одним subItem (не сгруппированная
  стопка). Внутри такой ячейки данные позиции склада имеют свой DisplayType, нас
  интересует параметр (DisplayType === Parameter). Если такие параметры встречаются
  минимум в двух разных колонках, их подстроки стоят бок о бок по горизонтали и
  должны равняться по высоте, как верхнеуровневые параметры.

  Здесь дополнительно выравниваем ЧИСЛО подстрок: один параметр может нести список
  значений (например s/n: несколько номеров на один ParentRecordId), и тогда такая
  колонка выше соседних. Соседние колонки добиваем пустышками снизу до того же
  числа подстрок, чтобы сработало построчное выравнивание высот по индексу
  (useRowParameterHeights пишет --qtl-psub-{idx}-h, плейсхолдеры подтягивают высоту).

  Важно: НЕ переупорядочиваем по ParentRecordId и не схлопываем список (как делает
  верхнеуровневая normalizeParameterCellsAcrossColumns для одиночных значений) —
  здесь в каждой колонке обычно одна запись (категория одной позиции склада), а
  «список» это её значения, которые терять нельзя. Поэтому просто добиваем снизу.

  Сгруппированные колонки (несколько subItems друг под другом) сюда не попадают:
  там параметры вертикально независимы и выравнивать их нельзя.

  Возвращает true, если такие параметры есть минимум в двух колонках.
*/
function normalizeStandaloneStorageParamsAcrossColumns(
  cells: Record<string, LiteCellGroup>,
): boolean {
  const PARAM_DT = TableDataCellDisplayType.Parameter;
  const DOSI = TableDataCellDisplayType.DataOfStorageItem;
  // ссылки на внутренние параметрические gen_el (CellDataOfStorageItem) одиночных колонок
  const genEls: Array<{ ItemData: CellDataParameter[] }> = [];

  for (const groupKey of Object.keys(cells)) {
    const g = cells[groupKey];
    if (g.subItems.length !== 1) continue;
    const cell = g.subItems[0];
    if (cell.DisplayType !== DOSI) continue;
    const data = (cell.Data || []) as Array<{ DisplayType?: number; ItemData?: unknown }>;
    // первый внутренний параметр (обычно он единственный на колонку)
    const genEl = data.find(
      (gen) => gen && gen.DisplayType === PARAM_DT && Array.isArray(gen.ItemData),
    ) as { ItemData: CellDataParameter[] } | undefined;
    if (genEl) genEls.push(genEl);
  }

  if (genEls.length < 2) return false;

  let maxLen = 0;
  for (const ge of genEls) {
    if (ge.ItemData.length > maxLen) maxLen = ge.ItemData.length;
  }

  // добиваем более короткие списки пустышками снизу до общего числа подстрок
  if (maxLen > 1) {
    for (const ge of genEls) {
      if (ge.ItemData.length >= maxLen) continue;
      const padded = ge.ItemData.slice();
      while (padded.length < maxLen) {
        padded.push({
          ParentRecordId: undefined,
          Value: "",
          ValueType: 0,
          __placeholder: true,
        } as unknown as CellDataParameter);
      }
      ge.ItemData = padded;
    }
  }

  return true;
}
